import React, { useEffect, useState } from "react";
import GridTable from "cvmgmt-cube-grid-table";
import { Pagination } from "cprime-foundational-components";
import CprimeButton from "../Common/CprimeButton";
import { Stack } from "@mui/material";
import { GridTableDataType } from "cvmgmt-txn-history-ui-lib/types/Packages/GridTable/types";
import { ColumnFilter, SortingState } from "@tanstack/react-table";
import {
  downloadApplication,
  downloadApplicationTemplate,
  getPublishHistoryData,
} from "../../services/RequestAccess/RequestAccess";
import { formatFilterValue } from "../../utils/formatFilters";
import { useNavigate } from "react-router-dom";
import { triggerToast } from "../../utils/toast";
import { downloadBlobFile } from "../../utils/commonUtil";
import { MdOutlineFileDownload, MdOutlineFileUpload } from "react-icons/md";
import UploadRequestDialog from "../ImportHistory/UploadRequest";
import { useSelector } from "react-redux";
import { getEnv } from "../../Constants/env";

interface ContextProps {
  appId: number;
  appName?: string;
  isAccessible?: boolean;
}

const PublishHistory: React.FC<ContextProps> = ({
  appId,
  appName = "",
  isAccessible = true,
}) => {
  const navigate = useNavigate();
  const [gridData, setGridData] = useState<GridTableDataType>({
    columns: [] as GridTableDataType["columns"],
    rows: [],
    totalCount: 0,
  });
  const [filters, setFilters] = useState<ColumnFilter[]>([]);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [pagination, setPagination] = useState({ page: 0, rowsPerPage: 25 });
  const [refresh, setrefresh] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [uploadRequestDialogIsOpen, setUploadRequestDialogIsOpen] =
    useState<boolean>(false);

  const { env: subEnv } = useSelector((state: any) => state?.createApplication);
  const getEnvType = getEnv();
  const env = getEnvType?.envtype?.toUpperCase();
  const normalizedEnv = env === "LOCAL" ? "DEV" : env === "SIT" ? subEnv : env;

  function getCSTTimestamp() {
    const now = new Date();
    const options: Intl.DateTimeFormatOptions = {
      timeZone: "America/Chicago",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    };
    const parts = new Intl.DateTimeFormat("en-US", options).formatToParts(now);
    const map = Object.fromEntries(
      parts?.map((p) => [p.type, p.value]),
    ) as Record<string, string>;

    return `${map.year}${map.month}${map.day}T${map.hour}${map.minute}${map.second}`;
  }

  function getFilenameFromContentDisposition(
    contentDispositionHeader?: any,
  ): string | null {
    if (!contentDispositionHeader) return null;

    const utf8NameMatch = contentDispositionHeader.match(
      /filename\*=UTF-8''([^;]+)/i,
    );
    if (utf8NameMatch?.[1]) {
      return decodeURIComponent(utf8NameMatch[1].replace(/"/g, "").trim());
    }

    const nameMatch = contentDispositionHeader.match(/filename=([^;]+)/i);
    if (nameMatch?.[1]) {
      return nameMatch[1].replace(/"/g, "").trim();
    }

    return null;
  }

  // const handleExportApplication = async () => {
  //   const payload = {
  //     appId: String(appId),
  //     appname: appName,
  //     environment: normalizedEnv,
  //   };

  //   try {
  //     const response = await exportApplication(payload);
  //     const headers =
  //       typeof response?.headers?.toJSON === "function"
  //         ? response.headers.toJSON()
  //         : response?.headers;

  //     const contentDisposition =
  //       headers?.["content-disposition"] ||
  //       headers?.["Content-Disposition"] ||
  //       "";

  //     const derivedFileName =
  //       getFilenameFromContentDisposition(contentDisposition);

  //     if ([200, 201].includes(response?.status)) {
  //       downloadBlobFile(
  //         response.data,
  //         derivedFileName ||
  //           `${appName}-${normalizedEnv}-${getCSTTimestamp()}-CST.xlsx`,
  //       );
  //       triggerToast("File exported successfully", "success");
  //     }
  //   } catch (error) {
  //     triggerToast("File exported failed", "error");
  //   }
  // };

  const handleDownloadApplicationTemplate = async () => {
    try {
      const response = await downloadApplicationTemplate();
      if ([200, 201].includes(response?.status)) {
        downloadBlobFile(response.data, "Application_Template.xlsx");
        triggerToast("Template downloaded successfully", "success");
      }
    } catch (err) {
      triggerToast("Template download failed", "error");
    }
  };

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const resp = await getPublishHistoryData(
        {
          sortingFilter: {
            filter:
              filters?.length > 0
                ? (filters || [])?.map((item: any) =>
                    formatFilterValue(item?.value, item?.id),
                  )
                : [],
            sort: sorting?.map((item: any) => {
              return {
                columnName: item?.id,
                type: item?.desc ? "DESC" : "ASC",
              };
            }),
            page: {
              limit: pagination?.rowsPerPage,
              offset: pagination?.page * pagination?.rowsPerPage,
            },
          },
        },
        appId,
      );

      const gridResult = {
        ...resp?.data,
        columns: resp?.data?.columns?.map((column: any) => {
          return {
            ...column,
            size: column?.size ?? 200,
            minSize: column?.minSize ?? 200,
          };
        }),
        rows: resp?.data?.rows?.map((item: any) => {
          const rowActions = Array.isArray(item?.action) ? item.action : [];
          return {
            ...item,
            action_id: item?.action,
            action: rowActions.map((action: any) => {
              return {
                ...action,
                disabled: !isAccessible,
              };
            }),
          };
        }),
      };
      setGridData(gridResult);
      setrefresh((perv) => perv + 1);
    } catch (error: any) {
      triggerToast(error?.message || "Unable to load publish history", "error");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const timeout = setTimeout(() => {
      fetchData();
    }, 300);

    return () => clearTimeout(timeout);
  }, [sorting, pagination, filters, isAccessible]);

  useEffect(() => {
    setPagination({
      page: 0,
      rowsPerPage: pagination?.rowsPerPage,
    });
  }, [filters]);

  return (
    <>
      <GridTable
        data={gridData ?? {}}
        actions={[
          {
            event: "save",
            component: (
              <Stack direction="row" spacing={1.5} fontWeight="bold">
                <CprimeButton
                  composition="outline"
                  variant="tertiary"
                  onClick={handleDownloadApplicationTemplate}
                  endIcon={
                    <MdOutlineFileDownload
                      style={{ fontSize: "16px" }}
                      id="ApplicationTemplate-icon-download"
                    />
                  }
                  id="ApplicationTemplate"
                  disabled={!isAccessible}
                >
                  Application Template
                </CprimeButton>
                {/* <CprimeButton
                  composition="outline"
                  variant="tertiary"
                  onClick={handleExportApplication}
                  endIcon={
                    <MdOutlineFileDownload
                      style={{ fontSize: "16px" }}
                      id="export-icon-download"
                    />
                  }
                  id="Export"
                >
                  Export
                </CprimeButton> */}
                <CprimeButton
                  composition="outline"
                  variant="tertiary"
                  id="Import"
                  size="medium"
                  onClick={() => setUploadRequestDialogIsOpen(true)}
                  endIcon={
                    <MdOutlineFileUpload
                      style={{ fontSize: "16px" }}
                      id="icon-upload"
                    />
                  }
                  disabled={!isAccessible}
                >
                  Import
                </CprimeButton>
                <CprimeButton
                  composition="outline"
                  variant="tertiary"
                  size="medium"
                  onClick={() => {
                    navigate(`/application-list/app-${appId}/publish/compare`);
                  }}
                  disabled={!isAccessible}
                >
                  Compare Published
                </CprimeButton>
                <CprimeButton
                  composition="outline"
                  variant="tertiary"
                  size="medium"
                  onClick={() => {
                    navigate(`/application-list/app-${appId}/publish/review`);
                  }}
                  disabled={!isAccessible}
                >
                  Review & Publish
                </CprimeButton>
              </Stack>
            ),
          },
        ]}
        style={{
          height: "calc(100vh - 34rem)",
          maxWidth: "calc(100vw - 4rem)",
        }}
        refresh={refresh}
        customPayload={true}
        isCustomizeColumns={false}
        title="Publish History"
        showHeader={true}
        filters={filters}
        sorting={sorting}
        setSorting={setSorting}
        setFilters={setFilters}
        pagination={pagination}
        loading={isLoading}
        onRowClick={(_, row: any) => {
          const rowData = row?.original;
          navigate(
            `/application-list/app-${appId}/publish/view${
              rowData?.id ? `?version=${rowData?.id}` : ""
            }`,
          );
        }}
        isPagination={false}
        showLoader={false}
        cellActions={[
          {
            event: "Restore",
            callback: (event, row) => {
              const rowData = row?.row?.original;
              navigate(
                `/application-list/app-${appId}/publish/restore?version=${rowData?.id}`,
              );
            },
            icon: false,
          },
          {
            event: "Review",
            callback: (event, row) => {
              const rowData = row?.row?.original;
              navigate(
                `/application-list/app-${appId}/publish/approve?version=${rowData?.id}`,
              );
            },
            icon: false,
          },
          {
            event: "Download",
            callback: (event, row) => {
              const rowData = row?.row?.original;
              // Ensure no Promise is returned by the callback
              (async () => {
                try {
                  if (!rowData?.id) return;
                  const response = (await downloadApplication(
                    rowData?.id,
                  )) as any;
                  const headers =
                    typeof response?.headers?.toJSON === "function"
                      ? response.headers.toJSON()
                      : response?.headers;

                  const contentDisposition =
                    headers?.["content-disposition"] ||
                    headers?.["Content-Disposition"] ||
                    "";

                  const derivedFileName =
                    getFilenameFromContentDisposition(contentDisposition);

                  if ([200, 201].includes(response?.status)) {
                    downloadBlobFile(
                      response.data,
                      derivedFileName ||
                        `${appName}-${normalizedEnv}-${getCSTTimestamp()}-CST.xlsx`,
                    );
                    triggerToast("File exported successfully", "success");
                  }
                } catch (err: any) {
                  triggerToast(err?.message || "Something went wrong", "error");
                }
              })(); // Immediately invoking the async function
            },
            icon: false,
          },
          {
            event: "Withdraw",
            callback: (event, row) => {
              const rowData = row?.row?.original;
              navigate(
                `/application-list/app-${appId}/publish/withdraw?version=${rowData?.id}`,
              );
            },
            icon: false,
          },
        ]}
      />
      <Pagination
        sx={{ width: "100%" }}
        {...{
          ...pagination,
          count: gridData?.totalCount ?? 0,
          rowsPerPageOptions: [25, 50, 100, 250],
          onPageChange: (_e, number) =>
            setPagination({ ...pagination, page: number }),
          onRowsPerPageChange: (evt) =>
            setPagination({
              page: 0,
              rowsPerPage: Number(evt.target.value ?? 25),
            }),
        }}
      />
      {uploadRequestDialogIsOpen && (
        <UploadRequestDialog
          open={uploadRequestDialogIsOpen}
          onClose={() => setUploadRequestDialogIsOpen(false)}
          appId={appId}
        />
      )}
    </>
  );
};

export default PublishHistory;
